import { useState, useEffect, useRef } from 'react'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { logger } from '../../lib/logger-client.js'
import { submitSessionLocation } from '../../lib/submit-session-location.js'

export type LocationData = { latitude: number; longitude: number; city?: string } | null

/** Canonical location view plus one browser-geolocation command after consent. */
export function useLocationState(_liveServiceRef: React.RefObject<unknown>) {
  const [locationData, setLocationData] = useState<LocationData>(null)
  const submissionRef = useRef<Promise<unknown> | null>(null)

  useEffect(() => {
    let cancelled = false

    const syncFromProjection = (
      snapshot: ReturnType<typeof clientSessionViewStore.getSnapshot>
    ) => {
      if (snapshot.location) {
        setLocationData(snapshot.location)
        return
      }
      if (!snapshot.sessionId || !snapshot.sessionPermissions?.location || submissionRef.current) {
        return
      }

      submissionRef.current = clientSessionViewStore
        .acquireBrowserLocation()
        .then(async location => {
          if (!location || cancelled) return
          setLocationData(location)
          await submitSessionLocation(snapshot.sessionId!, location)
        })
        .catch(error => {
          logger.warn('[useLocationState] Canonical location command failed', {
            sessionId: snapshot.sessionId,
            error: error instanceof Error ? error.message : String(error),
          })
        })
        .finally(() => {
          submissionRef.current = null
        })
    }

    syncFromProjection(clientSessionViewStore.getSnapshot())
    const unsubscribe = clientSessionViewStore.subscribe(syncFromProjection)
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  return { locationData }
}
